import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Activity,
  AlertCircle,
  ArrowUpRight,
  Building2,
  CalendarClock,
  CreditCard,
  LoaderCircle,
  RefreshCw,
  Store,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import KpiCard from "@/components/common/KpiCard";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import OverviewDateRangePicker from "@/components/overview/OverviewDateRangePicker";
import {
  formatRange,
  presetRange,
} from "@/components/overview/dateRangeModel";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useApp } from "@/context/AppContext";
import { getAdminOverview } from "@/lib/api";
import { fmtNumber, timeAgo } from "@/lib/formatters";
import {
  amountForCurrency,
  buildRevenueChartData,
  formatMoneyMinor,
  formatMoneyTotals,
} from "@/pages/overviewModel";

const CHART_COLORS = ["#0E5B52", "#0284C7", "#CA8A04", "#7C3AED", "#DC2626"];

function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Africa/Lagos";
  } catch {
    return "Africa/Lagos";
  }
}

function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function LoadingOverview() {
  return (
    <div className="space-y-6" aria-label="Loading admin overview" aria-live="polite" role="status">
      <span className="sr-only">Loading admin overview</span>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="space-y-2">
          <Skeleton className="h-3 w-32 rounded-full" />
          <Skeleton className="h-9 w-72 max-w-full rounded-lg" />
          <Skeleton className="h-4 w-96 max-w-full rounded-full" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-11 w-[248px] rounded-xl" />
          <Skeleton className="h-11 w-11 rounded-xl" />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Skeleton className="h-[210px] lg:col-span-5 rounded-xl" />
        <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-3">
          {Array.from({ length: 6 }, (_, index) => (
            <div key={index} className="h-[99px] rounded-xl border border-[#0E5B52]/5 bg-white p-4 space-y-3">
              <div className="flex justify-between">
                <Skeleton className="h-3 w-20 rounded-full" />
                <Skeleton className="h-8 w-8 rounded-lg" />
              </div>
              <Skeleton className="h-7 w-14 rounded-md" />
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-8 rounded-xl bg-white p-5">
          <div className="flex justify-between">
            <div className="space-y-2">
              <Skeleton className="h-3 w-28 rounded-full" />
              <Skeleton className="h-5 w-36 rounded-md" />
            </div>
            <Skeleton className="h-9 w-28 rounded-lg" />
          </div>
          <Skeleton className="mt-5 h-64 w-full rounded-xl" />
        </div>
        <div className="col-span-12 lg:col-span-4 rounded-xl bg-white p-5">
          <Skeleton className="h-3 w-24 rounded-full" />
          <Skeleton className="mt-2 h-5 w-32 rounded-md" />
          <div className="mt-6 space-y-3">
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton key={index} className="h-16 w-full rounded-xl" />
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-4">
        <Skeleton className="col-span-12 lg:col-span-5 h-52 rounded-xl" />
        <Skeleton className="col-span-12 lg:col-span-7 h-52 rounded-xl" />
      </div>
    </div>
  );
}

function ErrorOverview({ error, onRetry, isRetrying }) {
  return (
    <div className="card-elev rounded-xl p-10 text-center" role="alert">
      <AlertCircle className="w-10 h-10 text-red-500 mx-auto" />
      <div className="font-display text-xl font-semibold text-[#064E3B] mt-4">
        Unable to load the overview
      </div>
      <p className="text-sm text-[#6B7280] mt-2">
        {error?.message || "The overview service is temporarily unavailable."}
      </p>
      <Button className="mt-5 bg-[#0E5B52] hover:bg-[#0A4640]" onClick={onRetry}>
        <RefreshCw className={`w-4 h-4 mr-2 ${isRetrying ? "animate-spin" : ""}`} />
        Try again
      </Button>
    </div>
  );
}

export default function Overview() {
  const navigate = useNavigate();
  const { currentAdmin, logout } = useApp();
  const [dateRange, setDateRange] = useState(() => presetRange(14));
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const timezone = useMemo(browserTimezone, []);
  const fromDate = format(dateRange.from, "yyyy-MM-dd");
  const toDate = format(dateRange.to, "yyyy-MM-dd");

  const {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["admin-overview", fromDate, toDate, timezone],
    queryFn: ({ signal }) => getAdminOverview({
      from: fromDate,
      to: toDate,
      timezone,
      signal,
    }),
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (error?.status !== 401) return;
    void logout().finally(() => navigate("/", { replace: true }));
  }, [error, logout, navigate]);

  const financial = data?.financial || null;
  const currencies = useMemo(() => {
    const values = new Set();
    [financial?.mrr, financial?.revenue].forEach((totals) => {
      (totals || []).forEach((total) => values.add(total.currencyCode));
    });
    (financial?.planMix || []).forEach((plan) => values.add(plan.currencyCode));
    return Array.from(values).sort();
  }, [financial]);

  useEffect(() => {
    if (!currencies.length) {
      setSelectedCurrency("");
      return;
    }
    if (!currencies.includes(selectedCurrency)) {
      setSelectedCurrency(currencies[0]);
    }
  }, [currencies, selectedCurrency]);

  const revenueData = useMemo(() => (
    buildRevenueChartData(financial?.revenueSeries, selectedCurrency)
  ), [financial, selectedCurrency]);

  const selectedRevenueTotal = useMemo(() => (
    amountForCurrency(financial?.revenue, selectedCurrency)
  ), [financial, selectedCurrency]);

  const selectedPlanMix = useMemo(() => (
    (financial?.planMix || []).filter((plan) => plan.currencyCode === selectedCurrency)
  ), [financial, selectedCurrency]);

  const tasks = useMemo(() => {
    if (!data) return [];
    const items = [];
    if (financial?.failedAttempts > 0) {
      items.push({
        count: financial.failedAttempts,
        label: "Failed payment attempts in the last 30 days",
        path: "/payments",
        tone: "danger",
      });
    }
    if (financial?.pendingAttempts > 0) {
      items.push({
        count: financial.pendingAttempts,
        label: "Pending payment attempts need review",
        path: "/payments",
        tone: "warning",
      });
    }
    if (financial?.expiringIn7Days > 0) {
      items.push({
        count: financial.expiringIn7Days,
        label: "Subscriptions expire within seven days",
        path: "/subscriptions",
        tone: "warning",
      });
    }
    const recoveryShops = Number(data.shops.accessExpired || 0) +
      Number(data.shops.accessRequiresPayment || 0);
    if (recoveryShops > 0) {
      items.push({
        count: recoveryShops,
        label: "Shops require subscription recovery",
        path: "/subscriptions",
        tone: "danger",
      });
    }
    if (data.operations.activeSyncConflicts > 0) {
      items.push({
        count: data.operations.activeSyncConflicts,
        label: "Active inventory sync conflicts",
        path: "/sync",
        tone: "danger",
      });
    }
    return items;
  }, [data, financial]);

  if (isLoading) return <LoadingOverview />;
  if (isError && !data) {
    return <ErrorOverview error={error} onRetry={refetch} isRetrying={isFetching} />;
  }

  const shops = data?.shops || {};
  const network = data?.network || {};
  const adminName = currentAdmin?.name || "Admin";
  const recoveryShops = Number(shops.accessExpired || 0) +
    Number(shops.accessRequiresPayment || 0);

  return (
    <div className="space-y-6" data-testid="overview-page">
      <PageHeader
        eyebrow="Operations · Live"
        title={`${greeting()}, ${adminName}`}
        subtitle={`Authoritative platform metrics · ${data?.range?.timezone || timezone} · Updated ${timeAgo(data?.generatedAt)}`}
        actions={
          <div className="flex items-center gap-2">
            <OverviewDateRangePicker value={dateRange} onChange={setDateRange} />
            <Button
              data-testid="overview-refresh"
              variant="outline"
              className="h-11 w-11 rounded-xl border-[#0E5B52]/15 bg-white p-0 shadow-sm"
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label="Refresh overview"
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
            </Button>
          </div>
        }
      />

      {isError && data && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
          Refresh failed. Displaying the most recent available overview.
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        <div
          className="col-span-12 lg:col-span-5 rounded-xl p-5 text-white relative overflow-hidden flex flex-col min-h-[210px]"
          style={{ background: "linear-gradient(135deg, #0E5B52 0%, #0F4F47 55%, #0A3934 100%)" }}
        >
          <div
            className="absolute -right-20 -top-20 w-56 h-56 rounded-full opacity-[0.12] pointer-events-none"
            style={{ background: "radial-gradient(circle, #FDE047 0%, transparent 65%)" }}
          />
          <div className="relative z-10 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.14em] font-medium text-[#A7F3D0]">
              {financial ? "Monthly recurring revenue" : "Live shop network"}
            </div>
            <span className="flex items-center gap-1 text-[11px] font-medium text-[#A7F3D0] px-2 py-0.5 rounded-full bg-white/10">
              <Activity className="w-3 h-3" /> Live
            </span>
          </div>

          <div className="relative z-10 mt-4">
            <div className="font-display text-[30px] md:text-[34px] font-semibold tracking-tight leading-tight tabular-nums">
              {financial ? formatMoneyTotals(financial.mrr) : `${fmtNumber(shops.total)} shops`}
            </div>
            <div className="mt-1.5 text-[11px] text-white/60">
              {financial
                ? "Paid active subscriptions only; trials and admin grants are excluded."
                : "Financial metrics are restricted for your admin role."}
            </div>
          </div>

          <div className="relative z-10 mt-auto pt-4 border-t border-white/10 grid grid-cols-3 gap-2">
            <div>
              <div className="text-[15px] font-semibold font-display tabular-nums leading-none">
                {financial ? fmtNumber(financial.payingShops) : fmtNumber(shops.accessActive)}
              </div>
              <div className="text-[10px] uppercase tracking-[0.08em] text-white/55 mt-1">
                {financial ? "Paying shops" : "Active access"}
              </div>
            </div>
            <div className="border-l border-white/10 pl-3">
              <div className="text-[15px] font-semibold font-display tabular-nums leading-none">
                {fmtNumber(shops.newInRange)}
              </div>
              <div className="text-[10px] uppercase tracking-[0.08em] text-white/55 mt-1">
                New / {data?.range?.days || 0}d
              </div>
            </div>
            <div className="border-l border-white/10 pl-3">
              <div className="text-[15px] font-semibold font-display tabular-nums leading-none">
                {fmtNumber(recoveryShops)}
              </div>
              <div className="text-[10px] uppercase tracking-[0.08em] text-white/55 mt-1">Recovery</div>
            </div>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-3">
          <KpiCard
            testid="kpi-total-shops"
            label="Total shops"
            value={fmtNumber(shops.total)}
            delta={shops.newInRangeDeltaPct}
            hint={shops.newInRangeDeltaPct == null ? "No prior baseline" : `vs previous ${data?.range?.days}d`}
            icon={Store}
            accent="#A7F3D0"
          />
          <KpiCard testid="kpi-active-shops" label="Active access" value={fmtNumber(shops.accessActive)} icon={Activity} accent="#D9F99D" />
          <KpiCard testid="kpi-trial-shops" label="Trial" value={fmtNumber(shops.accessTrial)} icon={Users} accent="#FDE047" />
          <KpiCard testid="kpi-expired" label="Needs payment" value={fmtNumber(recoveryShops)} icon={AlertCircle} accent="#F5E6D3" />
          <KpiCard testid="kpi-branches" label="Active branches" value={fmtNumber(network.activeBranches)} icon={Building2} accent="#BAE6FD" />
          <KpiCard testid="kpi-staff" label="Active staff" value={fmtNumber(network.activeStaff)} hint={`${fmtNumber(network.invitedStaff)} invited`} icon={Users} accent="#E9D5FF" />
        </div>
      </div>

      {financial && (
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 lg:col-span-8 card-elev rounded-xl p-5">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.08em] font-medium text-[#6B7280]">Collected revenue</div>
                <div className="font-display text-base font-semibold text-[#064E3B] mt-1">
                  {formatRange(dateRange)}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {currencies.length > 1 && (
                  <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                    <SelectTrigger className="w-28 h-9" aria-label="Revenue currency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((currencyCode) => (
                        <SelectItem key={currencyCode} value={currencyCode}>{currencyCode}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="text-right">
                  <div className="font-display text-xl font-semibold text-[#064E3B]">
                    {formatMoneyMinor(selectedRevenueTotal, selectedCurrency || "NGN")}
                  </div>
                  <div className="text-xs text-[#4B5563]">successfully paid</div>
                </div>
              </div>
            </div>

            {selectedRevenueTotal === 0 ? (
              <div className="h-64 rounded-xl bg-[#F0F6F1]/60 flex items-center justify-center text-sm text-[#6B7280]">
                No paid subscription revenue in this period.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={revenueData} margin={{ top: 5, right: 8, left: -8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="overview-revenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0E5B52" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#0E5B52" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="rgba(14,91,82,0.08)" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#4B5563" }} axisLine={false} tickLine={false} minTickGap={28} />
                  <YAxis tick={{ fontSize: 11, fill: "#4B5563" }} axisLine={false} tickLine={false} width={48} />
                  <Tooltip
                    contentStyle={{ background: "#fff", border: "1px solid rgba(14,91,82,0.1)", borderRadius: 12, fontSize: 12 }}
                    formatter={(value) => formatMoneyMinor(Number(value) * 100, selectedCurrency)}
                  />
                  <Area type="monotone" dataKey="value" stroke="#0E5B52" strokeWidth={2.5} fill="url(#overview-revenue)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="col-span-12 lg:col-span-4 card-elev rounded-xl p-5">
            <div className="text-[10px] uppercase tracking-[0.08em] font-medium text-[#6B7280]">Subscriptions</div>
            <div className="font-display text-base font-semibold text-[#064E3B] mt-1">Active plan mix</div>
            {selectedPlanMix.length === 0 ? (
              <div className="mt-5 h-64 rounded-xl bg-[#F0F6F1]/60 flex items-center justify-center px-6 text-center text-sm text-[#6B7280]">
                No paid active plans.
              </div>
            ) : (
              <div className="space-y-3 mt-5">
                {selectedPlanMix.map((plan, index) => (
                <div key={plan.planId} className="rounded-xl border border-[#0E5B52]/10 p-3.5">
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: CHART_COLORS[index % CHART_COLORS.length] }} />
                      <span className="text-sm font-semibold text-[#064E3B] truncate">{plan.planName}</span>
                    </span>
                    <span className="text-sm font-bold text-[#064E3B]">{fmtNumber(plan.activeShops)}</span>
                  </div>
                  <div className="text-xs text-[#6B7280] mt-1 pl-[18px]">
                    {formatMoneyMinor(plan.mrrMinor, plan.currencyCode)} MRR
                  </div>
                </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-5 card-elev rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.08em] font-medium text-[#6B7280]">Operations</div>
              <div className="font-display text-base font-semibold text-[#064E3B] mt-1">Action queue</div>
            </div>
            <CalendarClock className="w-5 h-5 text-[#0E5B52]/50" />
          </div>
          <div className="space-y-2">
            {tasks.length === 0 ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-900">
                No subscription, payment, or sync exceptions need attention.
              </div>
            ) : tasks.map((task) => (
              <button
                key={`${task.path}:${task.label}`}
                onClick={() => navigate(task.path)}
                className={`w-full text-left p-3.5 rounded-xl border transition-all hover:shadow-sm ${
                  task.tone === "danger"
                    ? "bg-red-50 border-red-200 hover:bg-red-100/70"
                    : "bg-amber-50 border-amber-200 hover:bg-amber-100/70"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-[#064E3B]">
                    <strong className="tabular-nums">{fmtNumber(task.count)}</strong> {task.label}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-[#0E5B52] flex-shrink-0" />
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7 card-elev rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.08em] font-medium text-[#6B7280]">Acquisition</div>
              <div className="font-display text-base font-semibold text-[#064E3B] mt-1">New shops</div>
            </div>
            <Button variant="ghost" className="text-[#0E5B52]" onClick={() => navigate("/shops")}>
              View shops <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Today", value: shops.newToday, accent: "#A7F3D0" },
              { label: "Last 7 days", value: shops.newLast7Days, accent: "#BAE6FD" },
              { label: "Last 30 days", value: shops.newLast30Days, accent: "#FDE047" },
            ].map((metric) => (
              <div key={metric.label} className="rounded-xl p-4" style={{ background: `${metric.accent}30` }}>
                <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">{metric.label}</div>
                <div className="font-display text-2xl font-semibold text-[#064E3B] mt-1">{fmtNumber(metric.value)}</div>
              </div>
            ))}
          </div>

          <div className="mt-5 border-t border-[#0E5B52]/10 pt-4 space-y-1">
            {(data?.recentShops || []).length === 0 ? (
              <div className="py-6 text-center text-sm text-[#6B7280]">No recent shops.</div>
            ) : data.recentShops.map((shop) => (
              <button
                key={shop.id}
                data-testid={`recent-shop-${shop.id}`}
                onClick={() => navigate(`/shops/${shop.id}`)}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[#F0F6F1] transition-all"
              >
                <div className="w-9 h-9 rounded-lg bg-[#0E5B52] text-white flex items-center justify-center font-semibold text-xs">
                  {shop.name.split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase()}
                </div>
                <div className="flex-1 text-left min-w-0">
                  <div className="text-sm font-semibold text-[#064E3B] truncate">{shop.name}</div>
                  <div className="text-xs text-[#4B5563] truncate">
                    {shop.planName || "No plan"} · {shop.currencyCode}
                  </div>
                </div>
                <StatusBadge status={shop.accessStatus} />
                <span className="text-[11px] text-[#9CA3AF] hidden sm:inline">{timeAgo(shop.createdAt)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {financial && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <KpiCard
            label="Collected revenue"
            value={formatMoneyTotals(financial.revenue)}
            hint={formatRange(dateRange)}
            icon={Wallet}
            accent="#A7F3D0"
          />
          <KpiCard
            label="Expiring soon"
            value={fmtNumber(financial.expiringIn7Days)}
            hint="within 7 days"
            icon={CreditCard}
            accent="#FDE047"
          />
          <KpiCard
            label="Payment exceptions"
            value={fmtNumber(financial.failedAttempts + financial.pendingAttempts)}
            hint={`${fmtNumber(financial.failedAttempts)} failed · ${fmtNumber(financial.pendingAttempts)} pending`}
            icon={TrendingUp}
            accent="#FEE2E2"
          />
        </div>
      )}

      {isFetching && !isLoading && (
        <div className="fixed bottom-5 right-5 rounded-full bg-[#0E5B52] text-white px-3 py-2 text-xs shadow-lg flex items-center gap-2" role="status">
          <LoaderCircle className="w-3.5 h-3.5 animate-spin" /> Refreshing overview
        </div>
      )}
    </div>
  );
}
